const BASE = '/api';

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('plan_token');
  return fetch(BASE + url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  }).then(async res => {
    if (res.status === 401) { localStorage.removeItem('plan_token'); window.location.href = '/login'; }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      // `detail` is almost always a plain string across this API, but a handful of routes (the
      // platform in-use check) send a structured object instead — preserve it on the thrown Error
      // as `.detail` so a caller that needs it (a confirm dialog rendering a reference list) can
      // read it, without changing `.message` for every existing `catch(e){ alert(e.message) }` caller.
      const err = new Error(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Request failed'));
      err.detail = d.detail;
      throw err;
    }
    return res.json();
  });
}

export const digitalApi = {
  // Clients
  getClients: (showArchived = false) => authFetch(`/clients/${showArchived ? '?show_archived=true' : ''}`),
  getClient: (id) => authFetch(`/clients/${id}`),
  createClient: (data) => authFetch('/clients/', { method: 'POST', body: JSON.stringify(data) }),
  updateClient: (id, data) => authFetch(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archiveClient: (id) => authFetch(`/clients/${id}/archive`, { method: 'PATCH' }),
  deleteClient: (id) => authFetch(`/clients/${id}`, { method: 'DELETE' }),

  // Brands
  getBrands: (client_id) => authFetch(`/brands/${client_id ? `?client_id=${client_id}` : ''}`),
  getBrand: (id) => authFetch(`/brands/${id}`),
  createBrand: (data) => authFetch('/brands/', { method: 'POST', body: JSON.stringify(data) }),
  updateBrand: (id, data) => authFetch(`/brands/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBrand: (id) => authFetch(`/brands/${id}`, { method: 'DELETE' }),

  // Plans
  getPlans: (brand_id) => authFetch(`/plans/${brand_id ? `?brand_id=${brand_id}` : ''}`),
  getPlansOverview: () => authFetch('/plans/overview'),
  getAgencySummary: () => authFetch('/plans/agency-summary'),
  getPlan: (id) => authFetch(`/plans/${id}`),
  createPlan: (data) => authFetch('/plans/', { method: 'POST', body: JSON.stringify(data) }),
  updatePlan: (id, data) => authFetch(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  updatePlanCatalogue: (id, platformDbId, patch) => authFetch(`/plans/${id}/catalogue`, { method: 'PATCH', body: JSON.stringify({ platform_db_id: platformDbId, patch }) }),
  refreshPlanCatalogue: (id, entries) => authFetch(`/plans/${id}/catalogue/refresh`, { method: 'POST', body: JSON.stringify({ entries }) }),
  undoPlanCatalogueRefresh: (id) => authFetch(`/plans/${id}/catalogue/undo-refresh`, { method: 'POST' }),
  savePlan: (id, data) => authFetch(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  submitPlan: (id) => authFetch(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'review' }) }),
  approvePlan: (id) => authFetch(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'approved' }) }),
  rejectPlan: (id) => authFetch(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'draft' }) }),
  archivePlan: (id) => authFetch(`/plans/${id}/archive`, { method: 'PATCH' }),
  deletePlan: (id) => authFetch(`/plans/${id}`, { method: 'DELETE' }),
  clonePlan: (id) => authFetch(`/plans/${id}/clone`, { method: 'POST' }),

  // Platforms
  getPlatforms: (activeOnly = true) => authFetch(`/platforms/?active_only=${activeOnly}`),
  createPlatform: (data) => authFetch('/platforms/', { method: 'POST', body: JSON.stringify(data) }),
  updatePlatform: (id, data) => authFetch(`/platforms/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePlatform: (id, force = false) => authFetch(`/platforms/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

  // Trackers
  getTrackers: (activeOnly = true) => authFetch(`/trackers/?active_only=${activeOnly}`),
  createTracker: (data) => authFetch('/trackers/', { method: 'POST', body: JSON.stringify(data) }),
  updateTracker: (id, data) => authFetch(`/trackers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTracker: (id) => authFetch(`/trackers/${id}`, { method: 'DELETE' }),
  getTrackerEligibility: () => authFetch('/trackers/eligibility'),
  setTrackerEligibility: (data) => authFetch('/trackers/eligibility', { method: 'PATCH', body: JSON.stringify(data) }),

  // Client tracker overrides
  getClientTrackerOverrides: (clientId) => authFetch(`/clients/${clientId}/tracker-overrides`),
  updateClientTrackerOverrides: (clientId, data) => authFetch(`/clients/${clientId}/tracker-overrides`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteClientTrackerOverride: (clientId, trackerId) => authFetch(`/clients/${clientId}/tracker-overrides/${trackerId}`, { method: 'DELETE' }),

  // Sites
  getSites: () => authFetch('/sites/'),
  createSite: (data) => authFetch('/sites/', { method: 'POST', body: JSON.stringify(data) }),
  updateSite: (id, data) => authFetch(`/sites/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSite: (id) => authFetch(`/sites/${id}`, { method: 'DELETE' }),
  getSiteOverlaps: () => authFetch('/sites/overlaps'),
  updateSiteOverlaps: (items) => authFetch('/sites/overlaps', { method: 'PATCH', body: JSON.stringify(items) }),

  // Audiences
  getAudiences: (clientId) => authFetch(`/audiences/${clientId ? `?client_id=${clientId}` : ''}`),
  createAudience: (data) => authFetch('/audiences/', { method: 'POST', body: JSON.stringify(data) }),
  updateAudience: (id, data) => authFetch(`/audiences/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAudience: (id) => authFetch(`/audiences/${id}`, { method: 'DELETE' }),

  // Strategic Diagnostic
  getBrandHealthBenchmarks: (id) => authFetch(`/brands/${id}/brand-health/benchmarks`),
  createBrandHealth: (id, data) => authFetch(`/brands/${id}/brand-health`, { method: 'POST', body: JSON.stringify(data) }),
  getBrandHealthSnapshot: (id, healthId) => authFetch(`/brands/${id}/brand-health/${healthId}`),
  getLatestBrandHealth: (id) => authFetch(`/brands/${id}/brand-health/latest`),
  getPrefillResearch: (id) => authFetch(`/brands/${id}/prefill-research`, { method: 'POST' }),
  createStrategicBrief: (id, data) => authFetch(`/brands/${id}/strategic-brief`, { method: 'POST', body: JSON.stringify(data || {}) }),
  generateNarrative: (id, briefId, force) => authFetch(`/brands/${id}/strategic-briefs/${briefId}/narrative`, { method: 'POST', body: JSON.stringify(force ? { force: true } : {}) }),
  generateMarketResearch: (id, briefId) => authFetch(`/brands/${id}/strategic-briefs/${briefId}/market-research`, { method: 'POST' }),
  getStrategicBriefs: (id) => authFetch(`/brands/${id}/strategic-briefs`),
  getStrategicBrief: (id, briefId) => authFetch(`/brands/${id}/strategic-briefs/${briefId}`),
  deleteStrategicBrief: (id, briefId) => authFetch(`/brands/${id}/strategic-briefs/${briefId}`, { method: 'DELETE' }),

  // Client catalogue overrides
  getClientCatalogueOverrides: (clientId) => authFetch(`/clients/${clientId}/catalogue-overrides`),
  updateClientCatalogueOverrides: (clientId, data) => authFetch(`/clients/${clientId}/catalogue-overrides`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteClientCatalogueOverride: (clientId, platformId) => authFetch(`/clients/${clientId}/catalogue-overrides/${platformId}`, { method: 'DELETE' }),
  deleteClientCatalogueOverrideField: (clientId, platformId, field) => authFetch(`/clients/${clientId}/catalogue-overrides/${platformId}/${field}`, { method: 'DELETE' }),

  // Taxonomy (Gemius/GDE ZE-code, phase 1 of 3, 2026-10-05)
  getTaxonomyEntries: () => authFetch('/taxonomy/entries'),
  importTaxonomyEntries: (rows, dryRun) => authFetch('/taxonomy/import/entries', { method: 'POST', body: JSON.stringify({ rows, dry_run: dryRun }) }),
  getTaxonomyPlatformFormats: () => authFetch('/taxonomy/platform-formats'),
  importTaxonomyPlatformFormats: (rows, dryRun) => authFetch('/taxonomy/import/platform-formats', { method: 'POST', body: JSON.stringify({ rows, dry_run: dryRun }) }),
  getTaxonomyReservedCampaignIds: () => authFetch('/taxonomy/reserved-campaign-ids'),
  importTaxonomyReservedCampaignIds: (rows, dryRun) => authFetch('/taxonomy/import/reserved-campaign-ids', { method: 'POST', body: JSON.stringify({ rows, dry_run: dryRun }) }),
  getTaxonomyPlatformMappingSuggestions: () => authFetch('/taxonomy/platform-mapping-suggestions'),
  setTaxonomyPlatformFormatLink: (id, platformId) => authFetch(`/taxonomy/platform-formats/${id}/planiq-platform`, { method: 'PATCH', body: JSON.stringify({ platform_id: platformId }) }),
  setBrandTaxonomyLink: (brandId, taxonomyEntryId) => authFetch(`/brands/${brandId}/taxonomy-link`, { method: 'PATCH', body: JSON.stringify({ taxonomy_entry_id: taxonomyEntryId }) }),

  // Platform Accounts (per client)
  getClientPlatformAccounts: (clientId) => authFetch(`/clients/${clientId}/platform-accounts`),
  updateClientPlatformAccounts: (clientId, data) => authFetch(`/clients/${clientId}/platform-accounts`, { method: 'PATCH', body: JSON.stringify(data) }),
};
